import {
  Autocomplete,
  Box,
  Button,
  Grid,
  InputAdornment,
  styled,
  TableCell,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import MultiSelect from '../../../components/MultiSelect';
import PageHeader from '../../../components/PageHeader';
import SearchIcon from '@mui/icons-material/Search';
import Table from '../../../components/Table';
import { useEffect, useState } from 'react';
import { Company } from '../../../schema/company';
import { PersonType } from '../../../schema/person';
import { TableResponse } from '../../../schema/helper';
import { CovidApplicationTable } from '../../../schema/ptt';
import { api } from '../../../api';
import dayjs from 'dayjs';

const tableHead = [
  'เลขบัตรประชาชน',
  'ชื่อ นามสกุล',
  'บริษัท',
  'ผู้คุมงาน',
  'สถานะ',
  'วันเวลาที่เริ่มรับรองผล',
  'วันเวลาที่สิ้นสุดการรับรองผล'
  // 'Actions'
];

const ReportTempPeople = () => {
  const [filterID, setFilterID] = useState<string>('');
  const [filterName, setFilterName] = useState<string>('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [personTypes, setPersonTypes] = useState<PersonType[]>([]);
  const [filterCompanyIDList, setFilterCompanyIDList] = useState<string[]>([]);
  const [filterPersonTypeIDList, setFilterPersonTypeIDList] = useState<string[]>([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setData] = useState<TableResponse<CovidApplicationTable> | null>(null);

  const [isExportLoading, setIsExportLoading] = useState<boolean>(false);

  const handleExportCSVFile = () => {
    if (isExportLoading) {
      return;
    }
    const searchParams = new URLSearchParams();
    if (filterID) {
      searchParams.set('personal_id', `${filterID}`);
    }
    if (filterName) {
      searchParams.set('name', `${filterName}`);
    }
    if (filterCompanyIDList.length) {
      filterCompanyIDList.forEach((company_id) => {
        searchParams.append('company_id', `${company_id}`);
      });
    }
    if (filterPersonTypeIDList.length) {
      filterPersonTypeIDList.forEach((person_type_id) => {
        searchParams.append('person_type_id', `${person_type_id}`);
      });
    }
    setIsExportLoading(true);
    api
      .get(`/report-covid/export`, { params: searchParams, responseType: 'blob' })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'report-covid_export_data.xlsx');
        document.body.appendChild(link);
        link.click();
      })
      .finally(() => setIsExportLoading(false));
  };

  useEffect(() => {
    api.get<Company[]>(`/company`).then((res) => {
      const { data } = res;
      setCompanies(data);
    });
    api.get<PersonType[]>(`/person-type`).then((res) => {
      const { data } = res;
      setPersonTypes(data);
    });
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams();
    if (filterID) {
      searchParams.set('personal_id', `${filterID}`);
    }
    if (filterName) {
      searchParams.set('name', `${filterName}`);
    }
    if (filterCompanyIDList.length != 0) {
      filterCompanyIDList.forEach((company_id) => {
        searchParams.append('company_id', `${company_id}`);
      });
    }
    if (filterPersonTypeIDList.length != 0) {
      filterPersonTypeIDList.forEach((person_type_id) => {
        searchParams.append('person_type_id', `${person_type_id}`);
      });
    }
    searchParams.set('offset', `${page * rowsPerPage}`);
    searchParams.set('limit', `${rowsPerPage}`);

    api
      .get<TableResponse<CovidApplicationTable>>(`/report-covid`, { params: searchParams })
      .then((res) => {
        const { data } = res;
        setData(data);
      });
  }, [filterID, filterName, filterCompanyIDList, filterPersonTypeIDList, page, rowsPerPage]);

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return (
    <Container>
      <PageHeader text="รายงานการตรวจ Covid-19" />
      <Box display="flex" justifyContent="end">
        <Button variant="outlined" onClick={handleExportCSVFile} disabled={isExportLoading}>
          Export
        </Button>
      </Box>
      <Grid container spacing={2} my={3}>
        <Grid item md={3}>
          <TextField
            id="search-text-label"
            label="ค้นหาเลขบัตรประชาชน"
            variant="outlined"
            placeholder="ค้นหาเลขบัตรประชาชน"
            onChange={(event) => setFilterID(event.target.value)}
            fullWidth
            autoComplete="off"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Grid>
        <Grid item md={3}>
          <TextField
            id="search-text-label"
            label="ค้นหาข้อมูล"
            variant="outlined"
            placeholder="ค้นหาชื่อ"
            onChange={(event) => setFilterName(event.target.value)}
            fullWidth
            autoComplete="off"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Grid>
        <Grid item md={2}>
          <MultiSelect
            options={personTypes.map((data) => ({ id: data.id.toString(), name: data.type_name }))}
            onChange={(data) => setFilterPersonTypeIDList(data)}
            label="ประเภทบุคคล"
          />
        </Grid>
        <Grid item md={2}>
          <Autocomplete
            multiple
            disablePortal
            id="combo-box-demo"
            options={companies}
            sx={{ width: 300 }}
            getOptionLabel={(option) => option.company_name}
            renderInput={(params) => <TextField {...params} label="บริษัท" fullWidth />}
            onChange={(_, value) => setFilterCompanyIDList(value.map((data) => data.id.toString()))}
          />
        </Grid>
      </Grid>
      <Table
        heads={tableHead}
        count={data?.total ? data.total : 0}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      >
        {data?.data.map((data, index) => {
          return (
            <TableRow key={index}>
              <TableCell align="center">{data.personal_id}</TableCell>
              <TableCell align="center">{data.name}</TableCell>
              <TableCell align="center">{data.company}</TableCell>
              <TableCell align="center">{data.supervisor}</TableCell>

              <TableCell align="center">
                <Typography
                  variant="body2"
                  color={data.covid_test_result ? 'success.main' : 'error'}
                >
                  {data.covid_test_result ? 'ผ่าน' : 'ไม่ผ่าน'}
                </Typography>
              </TableCell>
              {/* <TableCell align="center">{startDatetime.toLocaleString()}</TableCell>
              <TableCell align="center">{endDatetime.toLocaleString()}</TableCell> */}
              <TableCell align="center">
                <Typography
                  variant="body2"
                  color={dayjs(data.covid_start_datetime) <= dayjs() ? 'success.main' : 'error'}
                >
                  {dayjs(data.covid_start_datetime).format('DD/MM/YYYY HH:mm')}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography
                  variant="body2"
                  color={dayjs(data.covid_end_datetime) > dayjs() ? 'success.main' : 'error'}
                >
                  {dayjs(data.covid_end_datetime).format('DD/MM/YYYY HH:mm')}
                </Typography>
              </TableCell>

              {/* <TableCell align="center" key={index}>
              <Button variant="contained" color="info">
                Info
              </Button>
            </TableCell> */}
            </TableRow>
          );
        })}
      </Table>
    </Container>
  );
};

export default ReportTempPeople;

const Container = styled('div')`
  padding: 16px;
`;
